import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FieldIcon, IconSearch } from './icons';

/**
 * Searchable popover listing catalogue fields that pass `accept` and can be
 * combined with the current selection. Escape or a click outside closes it.
 */
const FieldPicker = ({ catalogue, accept, statusOf, onPick, onClose, label = 'Choose a field' }) => {
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const term = search.trim().toLowerCase();
  const groups = useMemo(() => catalogue.cubes
    .map((cube) => ({
      cube,
      fields: [...cube.measures, ...cube.dimensions]
        .filter(accept)
        .filter((f) => statusOf(f).ok)
        .filter((f) => !term || `${cube.title} ${f.label} ${f.name}`.toLowerCase().includes(term)),
    }))
    .filter((g) => g.fields.length > 0), [catalogue, accept, statusOf, term]);

  return (
    <div
      ref={ref}
      className="rf-picker"
      role="dialog"
      aria-label={label}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}
    >
      <div className="rf-search rf-search--small">
        <IconSearch size={13} />
        <input ref={inputRef} aria-label="Search fields" placeholder="Search fields" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="rf-picker-list">
        {groups.length === 0 && <div className="rf-muted">No fields can be added here.</div>}
        {groups.map(({ cube, fields }) => (
          <div key={cube.name}>
            <div className="rf-picker-cube">{cube.title}</div>
            {fields.map((f) => (
              <button type="button" key={f.name} className={`rf-picker-item rf-field--${f.kind}`} onClick={() => onPick(f)}>
                <span className="rf-field-icon"><FieldIcon field={f} size={13} /></span>
                <span>{f.label}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FieldPicker;
